import React from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Toolbar from '@material-ui/core/Toolbar';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import { createMuiTheme, makeStyles } from "@material-ui/core/styles";
import TablePagination from '@material-ui/core/TablePagination';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

const theme = createMuiTheme({
    typography: {
      useNextVariants: true,
    },
  });

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function sumOfUsage(row){
  return Object.keys(row).reduce((sum,key)=>sum+(key !== 'name' && key !== 'balance' ? (row[key] ? row[key] : 0):0),0);
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

function createColumns(cores){
  const headRows = [{ id: 'name', numeric: false, disablePadding: true, label: 'Username' }];
  for (let i=0; i<cores.length; i+=1){
    headRows.push({ id: cores[i], numeric: true, disablePadding: false, label: cores[i] });
  }
  headRows.push({ id: 'balance', numeric: false, disablePadding: false, label: "Balance" });
  return headRows
}


const useToolbarStyles = makeStyles({
  actions: {
    color: theme.palette.text.secondary,
  }
});

const EnhancedTableToolbar = (props) => {
  const {setUsed, used} = props;
  const classes = useToolbarStyles();

  function handleUsed() {
    props.setPage(0);
    setUsed(!used);
  }

  return (
    <Toolbar>
      <div className={classes.spacer} />
      <div className={classes.actions}>
        <FormControlLabel
          control={
            <Checkbox
              checked={used}
              onChange={handleUsed}
              value="used"
              color="primary"
            />
          }
          label="Show only active users"
        />
      </div>
    </Toolbar>
  );
};

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort, headRows } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"/>
        {headRows.map(row => (
          <TableCell
            key={row.id}
            align={row.numeric || row.id === 'balance' ? 'right' : 'left'}
            padding={row.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === row.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === row.id}
              direction={order}
              onClick={createSortHandler(row.id)}
            >
              <b>{row.label}</b>
              {orderBy === row.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useStyles = makeStyles({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
});

export default function ReportTable(props) {
  const classes = useStyles();
  const { reports, cores, getUserReport, mode, coinsForEachServerPerMinute, numberWithCommas } = props;
  
  const headRows = createColumns(cores);
  const [order, setOrder] = React.useState('asc');
  const [used, setUsed] = React.useState(false);
  const [orderBy, setOrderBy] = React.useState('calories');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  
  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
    setPage(0);
  }

  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }

  const usage = [];
  for (let i =0; i < reports.length; i+=1){
    const info = reports[i].cores_info;
    const result = cores.reduce((server, key) => ({ ...server, [key]: 0}), {});
    for(let j = 0; j < info.length; j+=1){
      const core = info[j].cores ? info[j].cores : 90;
      result[core] += info[j].sum;
    }
    result.name = reports[i].name;
    result.balance = reports[i].balance;
    if (used && sumOfUsage(result) || !used){
      usage.push(result);
    }
  }

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, reports.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar used={used} setUsed={setUsed} setPage={setPage}/>
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={usage.length}
              headRows={headRows}
            />
            <TableBody>
              {stableSort(usage, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                 
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={labelId}
                      onClick={()=>{getUserReport(row.name)}}
                      style={{cursor: "pointer"}}
                    >
                      <TableCell padding="checkbox"/>
                      <TableCell component="th" id={labelId} scope="row" padding="none">
                        <b>{row.name}</b>
                      </TableCell>
                      {
                        cores.map((engine) =>
                        <TableCell align="right">{row[engine] === 0 ? <span style={{color: "#B9B9B9"}}>{0}</span>: 
                            <span>{numberWithCommas(mode === "min"? row[engine] : row[engine] * coinsForEachServerPerMinute[engine])}</span>}
                        </TableCell>)
                      }
                      <TableCell align="right">
                        <b>{row.balance}</b>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 49 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={usage.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'previous page',
          }}
          nextIconButtonProps={{
            'aria-label': 'next page',
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}